import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import CreateJointAccount from './CreateJointAccount';
import PendingInvitations from './PendingInvitations';
import WithdrawalApprovals from './WithdrawalApprovals';

const JointAccountsList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data: jointAccountsData, isLoading, error, refetch } = useQuery(
    'jointAccounts',
    async () => {
      const response = await apiClient.get('/api/joint-accounts');
      
      
      // Handle both old and new API response formats
      if (response.data.joint_accounts !== undefined && response.data.pending_invitations !== undefined) {
        // New format
        return response.data;
      } else if (Array.isArray(response.data)) {
        // Old format - just an array of joint accounts
        return {
          joint_accounts: response.data,
          pending_invitations: []
        };
      } else {
        // Fallback
        return {
          joint_accounts: [],
          pending_invitations: []
        };
      }
    },
    {
      onError: (error) => {
        console.error('Joint Accounts API Error:', error);
      }
    }
  );

  // Fetch pending withdrawal approvals
  const { data: pendingApprovalsData } = useQuery(
    'pendingApprovals',
    async () => {
      try {
        const response = await apiClient.get('/api/joint-transactions/pending');
        return response.data;
      } catch (error) {
        console.error('Pending Approvals API Error:', error);
        return { pending_transactions: [] };
      }
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      onError: (error) => {
        console.error('Pending Approvals API Error:', error);
      }
    }
  );

  const jointAccounts = jointAccountsData?.joint_accounts || [];
  const pendingInvitations = jointAccountsData?.pending_invitations || [];
  const pendingApprovals = pendingApprovalsData?.pending_transactions || [];

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'closed': return 'error';
      default: return 'default';
    }
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    refetch();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load joint accounts. Please try again.
        <br />
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Error: {error.message || 'Unknown error'}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, pb: { xs: 12, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 700, 
          color: '#FFFFFF',
          fontSize: { xs: '1.5rem', sm: '1.75rem' }
        }}>
          Joint Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              background: 'linear-gradient(135deg, #E8851A 0%, #E8A800 100%)'
            }
          }}
        >
          Create Joint Account
        </Button>
      </Box>



      {/* Pending Invitations */}
      <PendingInvitations 
        invitations={pendingInvitations} 
        onInvitationUpdate={refetch}
      />

      {/* Withdrawal Approvals */}
      <WithdrawalApprovals 
        transactions={pendingApprovals} 
        onApprovalUpdate={refetch}
      />

      {/* Joint Accounts List */}
      {jointAccounts?.length === 0 && pendingInvitations?.length === 0 ? (
        <Card sx={{ 
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No Joint Accounts Yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              {pendingInvitations?.length > 0 
                ? 'You have pending invitations. Accept them to join joint accounts, or create your own to start collaborating on investments.'
                : 'Create your first joint account to start collaborating on investments'
              }
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)'
              }}
            >
              Get Started
            </Button>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ width: '100%' }}>
          {jointAccounts?.map((account) => (
            <Card
              key={account.id}
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
                border: '1px solid #2B3139',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#F7931A',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 20px rgba(247, 147, 26, 0.2)'
                }
              }}
            >
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(247, 147, 26, 0.05)'
                  }
                }}
                onClick={() => navigate(`/joint-accounts/${account.id}`)}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: '#F7931A',
                      width: 56,
                      height: 56
                    }}
                  >
                    <AccountBalanceIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        {account.account_name}
                      </Typography>
                      <Chip
                        label={account.status}
                        color={getStatusColor(account.status)}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        Account #{account.account_number}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                        gap: { xs: 1, sm: 2 } 
                      }}>
                        <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                          Balance: {formatPrice(account.balance)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 16, color: '#6C7293' }} />
                          <Typography variant="caption" sx={{ color: '#6C7293' }}>
                            {account.active_members_count || 0} members
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                />

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/joint-accounts/${account.id}`);
                  }}
                  sx={{ color: '#F7931A' }}
                >
                  <VisibilityIcon />
                </IconButton>
              </ListItem>
            </Card>
          ))}
        </List>
      )}

      {/* Create Joint Account Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: { xs: 0, sm: '16px' },
            margin: { xs: 0, sm: 'auto' },
            height: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#FFFFFF', 
          borderBottom: '1px solid #2B3139',
          fontSize: { xs: '1.1rem', sm: '1.25rem' }
        }}>
          Create New Joint Account
        </DialogTitle>
        <DialogContent sx={{ 
          padding: { xs: '16px', sm: '24px' },
          overflowY: 'auto'
        }}>
          <CreateJointAccount
            onSuccess={handleCreateSuccess}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default JointAccountsList; 